import React, { useState } from 'react';
import { Modal, Button, Radio, Typography, message } from 'antd';
import { loadTossPayments } from '@tosspayments/payment-sdk';

const { Title, Text } = Typography;

// 환경변수에서 Client Key를 가져옵니다.
const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY || 'test_ck_qLlDJaYngrodavwenZm8ezGdRpXx';

const TossPaymentModal = ({ visible, onClose, grade, amount = 50000, user }) => {
    const [paymentMethod, setPaymentMethod] = useState('NORMAL'); // NORMAL (단건), BILLING (정기)
    const [loading, setLoading] = useState(false);

    const vatAmount = Math.round(amount * 0.1);
    const totalAmount = amount + vatAmount;

    // 고객 식별자 (등급 정보 포함)
    const customerKey = user
        ? `CUSTOMER_${grade?.grade_name || 'premium'}_${user.id}_${Date.now()}`
        : 'ANONYMOUS';

    const handlePaymentRequest = async () => {
        if (!user) {
            message.error('로그인 정보가 없습니다.');
            return;
        }

        setLoading(true);
        try {
            // TossPayments 코어 SDK 로드
            const tossPayments = await loadTossPayments(clientKey);

            const orderId = `ORDER_${grade?.grade_name || 'premium'}_${user.id}_${Date.now()}`;
            const successUrl = `${window.location.origin}/payment/success`;
            const failUrl = `${window.location.origin}/payment/fail`;

            if (paymentMethod === 'NORMAL') {
                // 단건 결제 요청 (팝업 방식)
                await tossPayments.requestPayment('카드', {
                    amount: totalAmount,
                    orderId: orderId,
                    orderName: `${(grade?.grade_name || 'Premium').toUpperCase()} 1개월 이용권`,
                    successUrl: successUrl,
                    failUrl: failUrl,
                    customerEmail: user.email || '',
                    customerName: user.name || '',
                });
            } else {
                // 정기 결제(빌링키) 인증 요청 (팝업 방식)
                await tossPayments.requestBillingAuth('카드', {
                    customerKey: customerKey,
                    successUrl: successUrl,
                    failUrl: failUrl,
                });
            }
        } catch (error) {
            console.error('결제 요청 에러:', error);
            // 사용자가 결제창을 닫은 경우 등
            if (error.code !== 'USER_CANCEL') {
                message.error(error.message || '결제 창을 여는 중 오류가 발생했습니다.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <Title level={4} style={{ margin: 0 }}>
                    {grade?.grade_name ? grade.grade_name.toUpperCase() : 'Premium'} 요금제 결제
                </Title>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={500}
            destroyOnClose
        >
            <div style={{ marginBottom: '24px', marginTop: '16px' }}>
                <Text strong style={{ display: 'block', marginBottom: '12px', fontSize: '15px' }}>
                    결제 방식을 선택해주세요
                </Text>
                <Radio.Group
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                    style={{ display: 'flex', gap: '12px' }}
                >
                    <Radio.Button
                        value="NORMAL"
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            height: '48px',
                            lineHeight: '48px',
                            borderRadius: '8px',
                        }}
                    >
                        단건 결제 (1개월)
                    </Radio.Button>
                    <Radio.Button
                        value="BILLING"
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            height: '48px',
                            lineHeight: '48px',
                            borderRadius: '8px',
                        }}
                    >
                        정기 결제 (구독)
                    </Radio.Button>
                </Radio.Group>

                {paymentMethod === 'BILLING' ? (
                    <div
                        style={{
                            marginTop: '16px',
                            padding: '16px',
                            background: '#f0f5ff',
                            borderRadius: '8px',
                        }}
                    >
                        <Text
                            style={{
                                color: '#2f54eb',
                                fontSize: '13px',
                                lineHeight: '1.5',
                                display: 'block',
                            }}
                        >
                            💡 정기 결제를 선택하시면 카드를 한 번 등록한 후, 매월 같은 날짜에
                            자동으로 결제되어 요금제가 자동 갱신됩니다.
                        </Text>
                    </div>
                ) : (
                    <div
                        style={{
                            marginTop: '16px',
                            padding: '16px',
                            background: '#f5f5f5',
                            borderRadius: '8px',
                        }}
                    >
                        <Text
                            style={{
                                color: '#595959',
                                fontSize: '13px',
                                lineHeight: '1.5',
                                display: 'block',
                            }}
                        >
                            💡 단건 결제를 선택하시면 1개월치 요금을 한 번만 결제합니다. 만료 시
                            다시 결제해야 합니다.
                        </Text>
                    </div>
                )}
            </div>

            <div style={{ marginTop: '12px', padding: '12px 16px', background: '#f9f9f9', borderRadius: '8px', fontSize: '13px', color: '#8c8c8c' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>공급가액</span>
                    <span>{amount.toLocaleString()}원</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <span>VAT (10%)</span>
                    <span>{vatAmount.toLocaleString()}원</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e8e8e8', color: '#262626', fontWeight: 700, fontSize: '14px' }}>
                    <span>최종 결제금액</span>
                    <span>{totalAmount.toLocaleString()}원</span>
                </div>
            </div>

            <Button
                type="primary"
                block
                onClick={handlePaymentRequest}
                loading={loading}
                style={{
                    height: 'auto',
                    minHeight: '60px',
                    padding: '18px 0',
                    marginTop: '12px',
                    borderRadius: '10px',
                    background: '#3182f6',
                    border: 'none',
                }}
            >
                <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                    {totalAmount.toLocaleString()}원 결제하기
                </span>
            </Button>
        </Modal>
    );
};

export default TossPaymentModal;
